import { io, Socket } from 'socket.io-client';
import { useAuthStore } from './auth.store';
import { useAdminAuthStore } from './admin-auth.store';
import { isCookieSessionToken } from './session-token';

function getSocketBaseUrl(): string {
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }

  return process.env.NEXT_PUBLIC_WS_URL || 'http://localhost:3001';
}

let socket: Socket | null = null;

function resolveSocketToken(tokenOverride?: string | null): string | null {
  if (typeof window === 'undefined') return null;
  return tokenOverride || useAuthStore.getState().token || useAdminAuthStore.getState().token;
}

function buildSocketAuth(token: string | null): { token?: string } {
  return token && !isCookieSessionToken(token) ? { token } : {};
}

export function getSocket(tokenOverride?: string | null): Socket {
  const token = resolveSocketToken(tokenOverride);
  const nextAuth = buildSocketAuth(token);

  if (!socket) {
    socket = io(`${getSocketBaseUrl()}/events`, {
      auth: nextAuth,
      // NO UPGRADE STEP (owner, 2026-09-08). The default is to open on polling and then upgrade
      // to a WebSocket. On the owner's network that upgrade dies — "WebSocket is closed before
      // the connection is established" — and it takes the ESTABLISHED session with it: the next
      // polling request carrying that same sid is answered 400, the client reconnects, upgrades,
      // dies again. While that loops nothing ticks and the header ticker falls back to its
      // 15-second REST refetch. The server logged no errors at all through it, because the
      // WebSocket was dying between the browser and the origin, not at the origin.
      //
      // Trying WebSocket FIRST removes the failure entirely: there is no working session for a
      // failed upgrade to destroy. Where a WebSocket connects it is used, exactly as before;
      // where it cannot, Socket.IO falls back to polling and simply stays there.
      //
      // Deliberately NOT `transports: ['polling']` for everyone, which was the other way to kill
      // the upgrade: at ~10 broadcasts a second, polling turns each client into a stream of HTTP
      // requests, and I/O contention is this platform's documented failure mode
      // (`docs/pending-tasks.md`). This keeps WebSocket for the clients who can use it.
      transports: ['websocket', 'polling'],
      upgrade: false,
      withCredentials: true,
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
    });
  } else if ((socket.auth as { token?: string } | undefined)?.token !== nextAuth.token) {
    socket.auth = nextAuth;
    if (socket.connected) {
      socket.disconnect().connect();
    } else {
      socket.connect();
    }
  } else if (!socket.connected && !socket.active) {
    // `active` is true while Socket.IO is connecting or has a reconnect scheduled. Without this
    // guard every caller of getSocket() during hydration — SessionManager, the access-gate strip,
    // the dashboard's own ticker — fired another connect() on a socket that was already
    // connecting, because `connected` stays false for the whole handshake window. Each extra
    // call aborts the handshake in flight ("WebSocket is closed before the connection is
    // established") and strands the session id it was negotiating, so the next polling request
    // carrying that sid is answered 400 and the session is closed. The client then reconnects
    // and the loop repeats, and while it does, no tick arrives and the ticker falls back to its
    // 15-second REST refetch — which is what the owner saw as a dead ticker (2026-09-08, traced
    // from his own network log: every socket.io row initiated by this line).
    //
    // Only ever connect a socket that has genuinely stopped trying.
    socket.connect();
  }
  return socket;
}

export function disconnectSocket(): void {
  socket?.disconnect();
  socket = null;
}
